import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  type AlertDialogProps,
  Box,
  Button,
  AlertDialog as ChakraAlertDialog,
  HStack,
  ModalCloseButton,
  type ModalHeaderProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useRef } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { openReplayStateLogger } from '../../hooks/open-replay';
import styles from './alert-dialog.module.css';

type AlertType = 'confirm' | 'actionless';

// Optional severity — renders a coloured icon next to the title.
type AlertStatus = 'warning' | 'error';

interface AlertProps {
  alertType: AlertType;
  /** Renders a warning/error icon next to the title. */
  status?: AlertStatus;
  onClose?: () => void;
  onAccept?: () => void;
  isOpen?: boolean;
  headerText: string;
  bodyContent: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  /** Overrides the Cancel button's Chakra color scheme. Defaults to 'navyBlue'. */
  cancelColorScheme?: string;
  /** Overrides the accept button's color scheme (e.g. 'orange' for a warning). */
  footerAcceptColorScheme?: string;
  /** Render only the accept button — no Cancel (e.g. an error acknowledgement). */
  hideCancel?: boolean;
  buttonLoading?: boolean;
  showCloseBtn?: boolean;
  chakraAlertProps?: Partial<AlertDialogProps>;
  headerProps?: Partial<ModalHeaderProps>;
  skipDefaultDisclosure?: boolean;
}

export type IRevelioAlert = {
  alertType: AlertType;
  status?: AlertStatus;
  isOpen: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  headerText: string;
  bodyContent?: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  footerAcceptColorScheme?: string;
  hideCancel?: boolean;
  onAlertTriggered?: () => void;
};

const DEFAULT_ACCEPT_TEXT = 'Ok';

interface AlertDialogStore {
  alert: IRevelioAlert | null;
}

const useAlertDialogStore = create<AlertDialogStore>()(
  devtools(
    openReplayStateLogger<AlertDialogStore>(
      () => ({
        alert: null,
      }),
      'alert_dialog'
    ),
    { name: 'AlertDialog' }
  )
);

export function showAlertDialog(alert: IRevelioAlert) {
  useAlertDialogStore.setState({ alert });
  alert.onAlertTriggered?.();
}

export const AlertDialog = ({
  chakraAlertProps = {},
  headerProps = {},
  isOpen,
  onClose,
  skipDefaultDisclosure = false,
  ...props
}: AlertProps) => {
  const cancelRef = useRef(null);
  const storeAlert = useAlertDialogStore((s) => s.alert);

  const alert = skipDefaultDisclosure ? null : storeAlert;

  const alertType = alert?.alertType ?? props.alertType;
  const status = alert?.status ?? props.status;
  const headerText = alert?.headerText ?? props.headerText;
  const bodyContent = alert?.bodyContent ?? props.bodyContent;
  const footerAcceptIsDestructive =
    alert?.footerAcceptIsDestructive ?? props.footerAcceptIsDestructive;
  const footerAcceptText =
    alert?.footerAcceptText ?? props.footerAcceptText ?? DEFAULT_ACCEPT_TEXT;
  const footerAcceptColorScheme =
    alert?.footerAcceptColorScheme ?? props.footerAcceptColorScheme;
  const hideCancel = alert?.hideCancel ?? props.hideCancel;
  const onAccept = alert?.onAccept ?? props.onAccept;

  const { onClose: disclosureOnClose, ...disclosureMain } = useDisclosure({
    onClose,
    isOpen: alert?.isOpen ?? isOpen,
  });

  const combinedOnClose = useCallback(() => {
    alert?.onClose?.();
    useAlertDialogStore.setState({ alert: null });
    disclosureOnClose();
  }, [alert, disclosureOnClose]);

  return (
    <ChakraAlertDialog
      onClose={combinedOnClose}
      {...chakraAlertProps}
      {...disclosureMain}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent className={styles.content}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" {...headerProps}>
            {status ? (
              <HStack spacing={2} align="center">
                <WarningTwoIcon
                  color={status === 'error' ? 'red.500' : 'orange.400'}
                  boxSize={5}
                  flexShrink={0}
                />
                <Box as="span">{headerText}</Box>
              </HStack>
            ) : (
              headerText
            )}
          </AlertDialogHeader>
          {props.showCloseBtn && <ModalCloseButton />}
          <AlertDialogBody>
            <Box pb={alertType === 'actionless' ? '12px' : '0'}>
              {bodyContent}
            </Box>
          </AlertDialogBody>
          {alertType !== 'actionless' && (
            <AlertDialogFooter>
              {!hideCancel && (
                <Button
                  ref={cancelRef}
                  colorScheme={props.cancelColorScheme ?? 'navyBlue'}
                  variant="ghost"
                  onClick={combinedOnClose}
                >
                  Cancel
                </Button>
              )}
              <Button
                colorScheme={
                  footerAcceptColorScheme ??
                  (footerAcceptIsDestructive ? 'red' : 'navyBlue')
                }
                onClick={() => {
                  combinedOnClose();
                  onAccept?.();
                }}
                ml={3}
                isLoading={props.buttonLoading}
              >
                {footerAcceptText}
              </Button>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </ChakraAlertDialog>
  );
};

export default AlertDialog;
